/**
 * ErrorMessage - Componente de exibição de erros no chat.
 * Responsabilidade: Renderizar feedback visual de erros com classificação de tipo,
 * botão de retry e indicador de status de rede.
 * 
 * Princípios: SRP — apenas renderização. Classificação vive no utilitário networkError.
 */
import React, { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AppIcon } from '@/components/icons/AppIcon';
import { classifyError, type ChatErrorType } from '@/utils/networkError';

interface ErrorMessageProps {
    message: string;
    onRetry?: () => void;
    onDismiss?: () => void;
    isOnline?: boolean;
    isRetrying?: boolean;
}

/** Mapeia tipo de erro para ícone e estilo visual */
const errorConfig: Record<ChatErrorType, { icon: 'activity' | 'lock' | 'clock' | 'alert' | 'info'; accent: string }> = {
    network: { icon: 'activity', accent: 'border-amber-500/50 bg-amber-50 dark:bg-amber-950/30' },
    auth: { icon: 'lock', accent: 'border-red-500/50 bg-red-50 dark:bg-red-950/30' },
    timeout: { icon: 'clock', accent: 'border-orange-500/50 bg-orange-50 dark:bg-orange-950/30' },
    server: { icon: 'alert', accent: 'border-red-500/50 bg-red-50 dark:bg-red-950/30' },
    validation: { icon: 'info', accent: 'border-blue-300 bg-card shadow-lg' },
    unknown: { icon: 'alert', accent: '' },
};

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
    message,
    onRetry,
    onDismiss,
    isOnline = true,
    isRetrying = false,
}) => {
    const classified = classifyError(message);
    const config = errorConfig[classified.type];
    const [autoDismissed, setAutoDismissed] = useState(false);

    // Auto-dismiss após 8 segundos para erros não críticos
    React.useEffect(() => {
        if (classified.type === 'validation' || classified.type === 'unknown') {
            const timer = setTimeout(() => {
                setAutoDismissed(true);
                onDismiss?.();
            }, 8000);
            return () => clearTimeout(timer);
        }
    }, [classified.type, onDismiss]);

    if (autoDismissed) return null;

    return (
        <div className={`relative mb-4 rounded-lg border p-4 animate-fade-in transition-all ${config.accent}`}>
            {/* Indicador de rede offline (barra superior sutil) */}
            {!isOnline && (
                <div className="absolute top-0 left-0 right-0 h-1 rounded-t-lg bg-gradient-to-r from-amber-400 to-red-400 animate-pulse" />
            )}

            <div className="flex items-start gap-3">
                {/* Ícone com cor contextual */}
                <div className={`mt-0.5 flex-shrink-0 ${classified.type === 'network' ? 'text-amber-600' : classified.type === 'auth' ? 'text-red-600' : 'text-error'}`}>
                    <AppIcon name={config.icon} className="h-5 w-5" />
                </div>

                <div className="flex-1 min-w-0">
                    {/* Título contextual */}
                    <h4 className="text-sm font-semibold text-default">
                        {classified.title}
                    </h4>

                    {/* Mensagem amigável */}
                    <p className="mt-1 text-sm leading-relaxed text-default">
                        {classified.message}
                    </p>

                    {/* Status de rede inline */}
                    {!isOnline && classified.type === 'network' && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                            <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                            Sem conexão com a internet
                        </div>
                    )}

                    {/* Ações */}
                    <div className="mt-3 flex items-center gap-3">
                        {classified.isRetryable && onRetry && (
                            <button
                                onClick={onRetry}
                                disabled={isRetrying}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md
                                    bg-primary/10 text-primary hover:bg-primary/20
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    transition-colors duration-200"
                            >
                                <AppIcon
                                    name="refresh"
                                    className={`h-3.5 w-3.5 ${isRetrying ? 'animate-spin' : ''}`}
                                />
                                {isRetrying ? 'Tentando...' : 'Tentar novamente'}
                            </button>
                        )}

                        {classified.type === 'auth' && (
                            <button
                                onClick={() => window.location.reload()}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md
                                    bg-primary/10 text-primary hover:bg-primary/20
                                    transition-colors duration-200"
                            >
                                <AppIcon name="refresh" className="h-3.5 w-3.5" />
                                Atualizar página
                            </button>
                        )}
                    </div>
                </div>

                {/* Botão fechar */}
                {onDismiss && (
                    <button
                        onClick={onDismiss}
                        className="flex-shrink-0 text-muted/60 hover:text-muted transition-colors"
                        aria-label="Fechar"
                    >
                        <AppIcon name="close" className="h-4 w-4" />
                    </button>
                )}
            </div>
        </div>
    );
};
